import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Eye,
  Heart,
  ThumbsUp,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
} from "lucide-react";
import { Dialog, DialogContent } from "../../ui/dialog.jsx";
import { Badge } from "../../ui/badge.jsx";
import ReactionButton from "./ReactionButton";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const VideoPlayer = ({
  isOpen,
  onClose,
  videoSrc,
  title,
  author,
  caption,
  likes,
  hearts,
  views,
  isOfficial,
  officialCategory,
  onLike,
  onHeart,
  liked = false,
  loved = false,
  isStudent = false,
  studentName,
  balagruha,
  metadata,
  createdAt,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  // Reset the player controls whenever a different pin is opened
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsMuted(false);
  }, [videoSrc]);

  // Determine what to display based on who uploaded the pin
  const getDisplayInfo = () => {
    // Check if uploaded by admin based on author.role
    if (author?.role === 'admin') {
      return {
        name: 'Created by Admin',
        location: null // No Balgruha for admin
      };
    }

    // Check if there's no author data (likely admin post without proper author population)
    if (!author || !author.name) {
      return {
        name: 'Created by Admin',
        location: null // No Balgruha for admin
      };
    }

    // Check if uploaded by coach (coach suggestion)
    if (metadata?.isCoachSuggestion || author?.role === 'coach') {
      return {
        name: studentName || 'Student Name',
        location: balagruha || 'Balgruha Name'
      };
    }

    // Default to student (regular student submission)
    return {
      name: studentName || author?.name || author || 'Student Name',
      location: balagruha || 'Balgruha Name'
    };
  };

  const displayInfo = getDisplayInfo();

  // Lightweight YouTube handling (no external deps)
  const isYouTubeUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    try {
      const u = new URL(url);
      return (
        /(^|\.)youtube\.com$/i.test(u.hostname) ||
        /(^|\.)youtu\.be$/i.test(u.hostname)
      );
    } catch (_) {
      return false;
    }
  };

  const toYouTubeEmbed = (url) => {
    try {
      const u = new URL(url);
      // youtu.be/<id>
      if (/^youtu\.be$/i.test(u.hostname)) {
        const id = u.pathname.replace(/^\//, "");
        return `https://www.youtube.com/embed/${id}`;
      }
      // shorts
      if (/\/shorts\//i.test(u.pathname)) {
        const id = u.pathname.split("/shorts/")[1]?.split("/")[0];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
      // standard watch?v=
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
    } catch (_) {
      // fall through
    }
    return url;
  };

  const officialCategoryLabel = officialCategory
    ? officialCategory === "mann-ki-baat"
      ? "Mann Ki Baat"
      : officialCategory === "op-ed"
        ? "Op Ed"
        : officialCategory === "isf-updates"
          ? "ISF Updates"
          : "Official"
    : null;

  const categoryLabel =
    officialCategoryLabel || (metadata?.tags && metadata.tags[0]) || "Video";

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
    } else {
      el.pause();
    }
  };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setIsMuted(el.muted);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen?.();
    }
  };

  const handleSeek = (e) => {
    const el = videoRef.current;
    const value = Number(e.target.value);
    if (el) el.currentTime = value;
    setCurrentTime(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none">
        <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-30 bg-white/90 hover:bg-white rounded-full p-2 transition-colors shadow-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          {/* Header */}
          <div className="px-6 pt-5 pb-3">
            <div className="flex items-start justify-between gap-4 pr-8">
              <h2 className="text-lg font-bold text-gray-900">{title}</h2>
              {isOfficial && (
                <Badge className="bg-purple-600 text-white text-xs px-2 py-1 whitespace-nowrap">
                  ISF Official
                </Badge>
              )}
            </div>
            {caption && (
              <p className="text-sm text-gray-500 mt-1 italic">{caption}</p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              {displayInfo.name}
              {displayInfo.location ? ` • ${displayInfo.location}` : ""}
              {" · "}
              {(createdAt ? new Date(createdAt) : new Date()).toLocaleDateString(
                "en-US",
                { month: "short", day: "numeric", year: "numeric" },
              )}
            </p>
          </div>

          {/* Video area */}
          <div
            ref={containerRef}
            className="relative w-full bg-black aspect-video"
          >
            <span className="absolute top-3 right-3 z-10 bg-black/60 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm">
              {categoryLabel}
            </span>

            {isYouTubeUrl(videoSrc) ? (
              <iframe
                title="YouTube video player"
                src={toYouTubeEmbed(videoSrc)}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <>
                <video
                  ref={videoRef}
                  src={videoSrc}
                  className="w-full h-full object-contain bg-black cursor-pointer"
                  onClick={togglePlay}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                  onLoadedMetadata={(e) =>
                    setDuration(e.currentTarget.duration || 0)
                  }
                />

                {!isPlaying && (
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
                    aria-label="Play video"
                  >
                    <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <Play className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" />
                    </span>
                  </button>
                )}

                {/* Custom control bar */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-4 pt-8 pb-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="text-white"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5" fill="currentColor" />
                    ) : (
                      <Play className="w-5 h-5" fill="currentColor" />
                    )}
                  </button>
                  <span className="text-white text-xs tabular-nums">
                    {formatTime(currentTime)}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step="0.1"
                    value={Math.min(currentTime, duration || 0)}
                    onChange={handleSeek}
                    className="flex-1 h-1 accent-red-500 cursor-pointer"
                    aria-label="Seek"
                  />
                  <span className="text-white text-xs tabular-nums">
                    {formatTime(duration)}
                  </span>
                  <button
                    type="button"
                    onClick={toggleMute}
                    className="text-white"
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={toggleFullscreen}
                    className="text-white"
                    aria-label="Fullscreen"
                  >
                    <Maximize className="w-5 h-5" />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-center gap-8 px-6 py-4 border-t border-gray-100">
            <div className="flex items-center gap-2 text-gray-500">
              <Eye className="w-5 h-5" />
              <span className="font-semibold text-sm">
                {views.toLocaleString()}
              </span>
            </div>
            <ReactionButton
              variant="like"
              pressed={liked}
              onClick={() => onLike && onLike()}
              className="flex items-center gap-2 text-pink-500 hover:opacity-80 transition-opacity"
              aria-label="Like"
              icon={<ThumbsUp className="w-5 h-5" />}
            >
              <span className="font-semibold text-sm">
                {likes.toLocaleString()}
              </span>
            </ReactionButton>
            <ReactionButton
              variant="love"
              pressed={loved}
              onClick={() => onHeart && onHeart()}
              className="flex items-center gap-2 text-green-600 hover:opacity-80 transition-opacity"
              aria-label="Love"
              icon={<Heart className="w-5 h-5" />}
            >
              <span className="font-semibold text-sm">{hearts}</span>
            </ReactionButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoPlayer;
